// Índice de Ajustes: cada configuración crece en su propia página.

import {
  Building2,
  FlaskConical,
  LineChart as LineChartIcon,
  LogOut,
  Palette,
  Wheat,
  Banknote,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { apiFetch } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';

function AjusteLink({
  icon: Icon,
  label,
  onClick,
}: {
  icon: typeof Palette;
  label: string;
  onClick: () => void;
}) {
  return (
    <section className="mt-4 rounded-lg border border-border bg-surface p-4">
      <button
        type="button"
        onClick={onClick}
        className="flex w-full items-center justify-between text-left"
      >
        <span className="flex items-center gap-2 text-base font-semibold text-text-primary">
          <Icon className="h-5 w-5 text-accent-icon" aria-hidden />
          {label}
        </span>
        <span aria-hidden className="text-muted-foreground">
          ›
        </span>
      </button>
    </section>
  );
}

export function AjustesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setUser = useAuthStore((s) => s.setUser);
  const clearTokens = useAuthStore((s) => s.clearTokens);
  const user = useAuthStore((s) => s.user);
  const canReadSettings =
    user?.rol === 'admin' || user?.rol === 'ganadero' || user?.rol === 'veterinario';

  async function onLogout() {
    try {
      await apiFetch('/v1/auth/logout', { method: 'POST' });
    } catch {
      // Aunque falle, limpiamos tokens localmente.
    }
    setUser(null);
    clearTokens();
    navigate('/login', { replace: true });
  }

  return (
    <main className="container mx-auto max-w-2xl px-4">
      <h1 className="text-xl font-bold text-text-primary">{t('ajustes.titulo')}</h1>
      <AjusteLink
        icon={Palette}
        label={t('ajustes.tema')}
        onClick={() => navigate('/ajustes/tema')}
      />
      {canReadSettings && (
        <>
          <AjusteLink
            icon={Building2}
            label={t('naves.titulo')}
            onClick={() => navigate('/ajustes/naves')}
          />
          <AjusteLink
            icon={LineChartIcon}
            label={t('ajustes.curva')}
            onClick={() => navigate('/ajustes/curva')}
          />
          <AjusteLink
            icon={Wheat}
            label={t('ajustes.curva_pienso')}
            onClick={() => navigate('/ajustes/pienso')}
          />
          <AjusteLink
            icon={Banknote}
            label={t('liquidacion.ajustes')}
            onClick={() => navigate('/ajustes/liquidacion')}
          />
        </>
      )}
      {import.meta.env.DEV && user?.rol === 'admin' && (
        <AjusteLink
          icon={FlaskConical}
          label={t('ajustes.iot_demo')}
          onClick={() => navigate('/demo/iot')}
        />
      )}
      <button
        type="button"
        onClick={onLogout}
        className="mt-6 flex w-full items-center justify-center gap-2 rounded-md border border-border px-4 py-3 text-base font-medium text-text-primary hover:bg-background"
      >
        <LogOut className="h-5 w-5" aria-hidden />
        {t('ajustes.sesion_cerrar', { usuario: useAuthStore.getState().user?.username ?? '' })}
      </button>
    </main>
  );
}

export default AjustesPage;
